import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DiffEditor, type DiffOnMount, type Monaco, type MonacoDiffEditor } from "@monaco-editor/react";
import type * as MonacoEditor from "monaco-editor";

import useTypedContext from "hooks/useTypedContext";
import { ThemeContext } from "views/Theme";

import { findKeyLine, parseYamlPath, stripYamlComments } from "../parseYamlPath";
import styles from "./styles.module.css";

type ExportToTemplateDiffEditorProps = {
  originalTemplate: string;
  migratedTemplate: string;
  warnings: string[];
  errors: string[];
};

const ExportToTemplateDiffEditor = ({
  originalTemplate,
  migratedTemplate,
  warnings,
  errors,
}: ExportToTemplateDiffEditorProps) => {
  const { isDarkMode, currentDarkModeSyntaxTheme, currentLightModeSyntaxTheme } =
    useTypedContext(ThemeContext);

  const [isEditorMounted, setIsEditorMounted] = useState(false);
  const strippedOriginalTemplate = useMemo(
    () => stripYamlComments(originalTemplate),
    [originalTemplate]
  );

  const diffEditorRef = useRef<MonacoDiffEditor | null>(null);
  const monacoRef = useRef<Monaco | null>(null);
  const warningDecorationsRef = useRef<MonacoEditor.editor.IEditorDecorationsCollection | null>(null);
  const errorDecorationsRef = useRef<MonacoEditor.editor.IEditorDecorationsCollection | null>(null);

  const handleEditorMount = useCallback<DiffOnMount>((editor, monaco) => {
    diffEditorRef.current = editor;
    monacoRef.current = monaco;
    warningDecorationsRef.current = editor.getOriginalEditor().createDecorationsCollection();
    errorDecorationsRef.current = editor.getModifiedEditor().createDecorationsCollection();
    editor.getOriginalEditor().updateOptions({ glyphMargin: true });
    editor.getModifiedEditor().updateOptions({ glyphMargin: true });
    setIsEditorMounted(true);
  }, []);

  useEffect(() => {
    if (!isEditorMounted) return;

    const monaco = monacoRef.current;
    const diffEditor = diffEditorRef.current;
    if (!monaco || !diffEditor) return;

    const originalModel = diffEditor.getOriginalEditor().getModel();
    const modifiedModel = diffEditor.getModifiedEditor().getModel();

    if (originalModel) {
      const warningAnnotations = warnings.map((message) => {
        const { key, depth } = parseYamlPath(message);
        const line = key ? findKeyLine(strippedOriginalTemplate, key, depth) : 1;
        return { message, line };
      });
      monaco.editor.setModelMarkers(
        originalModel,
        "yaml-migration",
        warningAnnotations.map(({ message, line }) => ({
          severity: monaco.MarkerSeverity.Warning,
          message,
          startLineNumber: line,
          startColumn: 1,
          endLineNumber: line,
          endColumn: Number.MAX_SAFE_INTEGER,
        }))
      );
      warningDecorationsRef.current?.set(
        warningAnnotations.map(({ message, line }) => ({
          range: new monaco.Range(line, 1, line, 1),
          options: {
            glyphMarginClassName: "yamlMigrationWarningGlyph",
            glyphMarginHoverMessage: { value: message },
            className: "yamlMigrationWarningLine",
            isWholeLine: true,
          },
        }))
      );
    }

    if (modifiedModel) {
      const errorAnnotations = errors.map((message) => {
        const { key, depth } = parseYamlPath(message);
        const line = key ? findKeyLine(migratedTemplate, key, depth) : 1;
        return { message, line };
      });
      monaco.editor.setModelMarkers(
        modifiedModel,
        "yaml-migration",
        errorAnnotations.map(({ message, line }) => ({
          severity: monaco.MarkerSeverity.Error,
          message,
          startLineNumber: line,
          startColumn: 1,
          endLineNumber: line,
          endColumn: Number.MAX_SAFE_INTEGER,
        }))
      );
      errorDecorationsRef.current?.set(
        errorAnnotations.map(({ message, line }) => ({
          range: new monaco.Range(line, 1, line, 1),
          options: {
            glyphMarginClassName: "yamlMigrationErrorGlyph",
            glyphMarginHoverMessage: { value: message },
            className: "yamlMigrationErrorLine",
            isWholeLine: true,
          },
        }))
      );
    }

    return () => {
      if (originalModel) monaco.editor.setModelMarkers(originalModel, "yaml-migration", []);
      if (modifiedModel) monaco.editor.setModelMarkers(modifiedModel, "yaml-migration", []);
      warningDecorationsRef.current?.clear();
      errorDecorationsRef.current?.clear();
    };
  }, [errors, warnings, strippedOriginalTemplate, migratedTemplate, isEditorMounted]);

  return (
    <div className={styles.wrapper}>
      <DiffEditor
        original={strippedOriginalTemplate}
        modified={migratedTemplate}
        language="yaml"
        theme={isDarkMode ? currentDarkModeSyntaxTheme : currentLightModeSyntaxTheme}
        keepCurrentOriginalModel
        keepCurrentModifiedModel
        onMount={handleEditorMount}
        options={{
          readOnly: true,
          renderSideBySide: true,
          useInlineViewWhenSpaceIsLimited: false,
          scrollBeyondLastLine: false,
          glyphMargin: true,
        }}
      />
    </div>
  );
};

export default ExportToTemplateDiffEditor;
